import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Plus, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth/hooks';
import { supabase } from '../../../lib/supabase';
import { StaffMember, StaffFormData } from './types';
import { validateStaffForm } from './validation';
import { StaffFormModal } from './components/StaffFormModal';
import { StaffCard } from './components/StaffCard';
import toast from 'react-hot-toast';

// -------------------------------------------------------------
// הגדרות תכניות: מספר מושבי STAFF (ללא המנהל/owner)
// basic  → מנהל בלבד → 0 אנשי צוות
// medium → מנהל + 2 אנשי צוות → 2
// vip    → מנהל + 4 אנשי צוות → 4  (+ extra_seats בעתיד)
// -------------------------------------------------------------
type PlanKey = 'basic' | 'medium' | 'vip';

const BASE_STAFF_SEATS: Record<PlanKey, number> = {
  basic: 0,
  medium: 2,
  vip: 4,
};

// רק סטטוסים שמקנים שימוש מלא
const VALID_STATUSES = new Set(['active', 'trialing'] as const);

// -------------------------------------------------------------
// Hook לניהול מגבלת צוות ותכנית
// מקבל user + businessId יציב (לא כל האובייקט business כדי למנוע הבהובים)
// -------------------------------------------------------------
function useTeamSeatLimit(user: any, businessId: string | null) {
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);
  const [planKey, setPlanKey] = useState<PlanKey | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [staffCount, setStaffCount] = useState(0);
  const [extraSeats, setExtraSeats] = useState(0);

  // נשמור "known good" כדי לא לאבד תכנית/סטטוס בהבהובי context
  const lastGood = useRef<{ planKey: PlanKey | null; status: string | null }>({
    planKey: null,
    status: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (!user?.id || !businessId) return;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        // 1) משיכת מנויים: קודם של העסק, אם אין—של המשתמש (pending)
        let subs: any[] = [];
        {
          const { data, error } = await supabase
            .from('subscriptions')
            .select('id, plan_id, status, current_period_end, metadata, created_at, business_id, user_id')
            .eq('business_id', businessId)
            .order('created_at', { ascending: false });
          if (error) {}
          if (data?.length) subs = data;
        }
        if (!subs.length) {
          const { data, error } = await supabase
            .from('subscriptions')
            .select('id, plan_id, status, current_period_end, metadata, created_at, business_id, user_id')
            .eq('user_id', user.id)
            .is('business_id', null)
            .order('created_at', { ascending: false });
          if (error) {}
          if (data?.length) subs = data;
        }

        // 2) בחר מנוי תקף (active/trialing ולא פג תוקף). אם אין—קח האחרון כ-fallback
        const now = Date.now();
        const sub =
          subs.find(
            (s) =>
              s.plan_id &&
              s.status &&
              VALID_STATUSES.has(s.status) &&
              s.current_period_end &&
              new Date(s.current_period_end).getTime() > now
          ) ?? subs[0];

        // 3) פתר plan_key מהטבלה
        let effectivePlanKey: PlanKey | null = null;
        let effectiveStatus: string | null = null;
        let extra = 0;

        if (sub?.plan_id) {
          const { data: planRow, error: planErr } = await supabase
            .from('subscription_plans')
            .select('plan_key')
            .eq('id', sub.plan_id)
            .maybeSingle();
          if (planErr) {}
          if (planRow?.plan_key && ['basic', 'medium', 'vip'].includes(planRow.plan_key)) {
            effectivePlanKey = planRow.plan_key as PlanKey;
            effectiveStatus = sub?.status ?? null;
            extra = Number(sub?.metadata?.extra_seats ?? 0) || 0;
          }
        }

        // 4) עדכון סטייט עם שמירת known-good
        if (effectivePlanKey && effectiveStatus) {
          lastGood.current = { planKey: effectivePlanKey, status: effectiveStatus };
          if (!cancelled) {
            setPlanKey(effectivePlanKey);
            setStatus(effectiveStatus);
          }
        } else {
          if (!cancelled) {
            setPlanKey(lastGood.current.planKey);
            setStatus(lastGood.current.status);
          }
        }
        if (!cancelled) setExtraSeats(extra);

        // 5) ספר STAFF בלבד (לא מנהל/owner)
        if (businessId) {
          const { count } = await supabase
            .from('users')
            .select('*', { head: true, count: 'exact' })
            .eq('business_id', businessId)
            .in('role', ['staff', 'employee']);
          if (!cancelled) setStaffCount(count ?? 0);
        }
      } catch (e: any) {

        if (!cancelled) setError(e?.message ?? 'Failed to load subscription/usage');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, businessId]);

  const allowedStaff = useMemo(() => {
    if (!planKey) return 0;
    const base = BASE_STAFF_SEATS[planKey];
    return base + (planKey === 'vip' ? extraSeats : 0);
  }, [planKey, extraSeats]);

  const canAddStaff = allowedStaff === 0 ? false : staffCount < allowedStaff;

  return { loading, error, planKey, status, staffCount, allowedStaff, canAddStaff, refreshSeats: () => {} };
}

// -------------------------------------------------------------
// טופס ברירת מחדל
// -------------------------------------------------------------
const defaultFormData: StaffFormData = {
  email: '',
  name: '',
  password: '',
  phone: '',
  title: '',
  description: '',
  specialties: [],
  settings: {
    rest_time: 15,
    max_daily_appointments: null,
    visible_in_public: true,
  },
  profile_image: null,
  services: [],
  hours: {
    use_business_hours: true,
    special_dates: [],
  },
};

// -------------------------------------------------------------
// קומפוננטת צוות
// -------------------------------------------------------------
function BusinessStaff() {
  const navigate = useNavigate();
  const { user, business } = useAuth();
  const [businessId, setBusinessId] = useState<string | null>(business?.id ?? null);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [services, setServices] = useState<any[]>([]);
  const [specialties, setSpecialties] = useState<any[]>([]);
  const [formData, setFormData] = useState<StaffFormData>(defaultFormData);
  const [formBusy, setFormBusy] = useState(false);

  // נעל businessId יציב כאשר מגיע מה־context
  useEffect(() => {
    if (!businessId && business?.id) {
      setBusinessId(business.id);
    }
  }, [business?.id, businessId]);

  // אם עדיין אין businessId – נשלוף מה־users (פעם אחת)
  useEffect(() => {
    let cancelled = false;
    async function ensureBusinessId() {
      if (businessId || !user?.id) return;
      const { data } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user.id)
        .maybeSingle();
      if (!cancelled && data?.business_id) {
        setBusinessId(data.business_id);
      }
    }
    ensureBusinessId();
    return () => { cancelled = true; };
  }, [user?.id, businessId]);

  // Hook למגבלת צוות (מבוסס businessId יציב)
  const {
    loading: seatLoading,
    error: seatError,
    planKey,
    status,
    staffCount,
    allowedStaff,
    canAddStaff,
  } = useTeamSeatLimit(user, businessId);

  // טען נתוני צוות
  const loadData = async (bizId: string) => {
    try {
      setLoadingStaff(true);

      // מנהל (admin) – תמיד ראשון
      console.log('🔵 [BusinessStaff/loadData] Loading admin for business_id:', bizId);

      const { data: adminData, error: adminError } = await supabase
        .from('users')
        .select(`
          id, name, email, phone, role, title, description,
          profile_image_url, specialties, settings, created_at, metadata
        `)
        .eq('business_id', bizId)
        .eq('role', 'admin')
        .maybeSingle();
      if (adminError) throw adminError;

      console.log('🔵 [BusinessStaff/loadData] ===== ADMIN DATA FROM DB =====');
      console.log('🔵 [BusinessStaff/loadData] Full adminData:', adminData);
      console.log('🔵 [BusinessStaff/loadData] Admin name field:', adminData?.name);
      console.log('🔵 [BusinessStaff/loadData] Admin email:', adminData?.email);
      console.log('🔵 [BusinessStaff/loadData] Admin metadata:', adminData?.metadata);

      // צוות (staff בלבד)
      const { data: staffData, error: staffError } = await supabase
        .from('users')
        .select(`
          id, name, email, phone, role, title, description,
          profile_image_url, specialties, settings, created_at
        `)
        .eq('business_id', bizId)
        .in('role', ['staff', 'employee'])
        .order('created_at');
      if (staffError) throw staffError;

      const staffList: StaffMember[] = [];
      if (adminData) {
        // קודם כל, ננסה לקחת את השם מ-metadata.full_name (הכי מדויק)
        const nameFromMetadata = adminData.metadata?.full_name;
        const finalName = nameFromMetadata || adminData.name || 'מנהל';

        console.log('🔵 [BusinessStaff/loadData] Name from metadata.full_name:', nameFromMetadata);
        console.log('🔵 [BusinessStaff/loadData] Final name that will be displayed:', finalName);

        staffList.push({
          id: adminData.id,
          name: finalName,
          email: adminData.email || '',
          phone: adminData.phone || '',
          role: 'admin',
          title: adminData.title || '',
          description: adminData.description || '',
          profile_image_url: adminData.profile_image_url || '',
          specialties: adminData.specialties || [],
          settings: adminData.settings || {},
          created_at: adminData.created_at || '',
        });
      } else {
        // fallback: מציג מנהל-placeholder
        staffList.push({
          id: 'admin-placeholder',
          name: 'מנהל',
          email: user?.email || '',
          phone: '',
          role: 'admin',
          title: '',
          description: '',
          profile_image_url: '',
          specialties: [],
          settings: {},
          created_at: '',
        });
      }

      if (staffData?.length) {
        staffList.push(
          ...staffData.map((m: any) => ({
            ...m,
            created_at: m.created_at || '',
          }))
        );
      }

      setStaff(staffList);

      // שירותים
      const { data: servicesData, error: servicesErr } = await supabase
        .from('services')
        .select('*')
        .eq('business_id', bizId)
        .order('name_he');
      if (servicesErr) throw servicesErr;
      setServices(servicesData || []);

      // תחומי התמחות
      const { data: specsData, error: specsErr } = await supabase
        .from('staff_specialties')
        .select('*')
        .eq('business_id', bizId)
        .order('name_he');
      if (specsErr) throw specsErr;
      setSpecialties(specsData || []);
    } catch (e: any) {

    } finally {
      setLoadingStaff(false);
    }
  };

  // טען נתונים כאשר יש businessId
  useEffect(() => {
    if (businessId) loadData(businessId);
  }, [businessId]);

  // Realtime: האזן לשינויים בטבלת users עבור אותו business
  useEffect(() => {
    if (!businessId) return;
    const ch = supabase
      .channel(`users-staff-${businessId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'users', filter: `business_id=eq.${businessId}` },
        async () => {
          await loadData(businessId);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [businessId]);

  const handleEdit = async (member: StaffMember) => {
    setEditingStaff(member);
    try {
      const { data: staffServices, error: servicesError } = await supabase
        .from('staff_services')
        .select('*')
        .eq('staff_id', member.id);
      if (servicesError) throw servicesError;

      const { data: staffHours, error: hoursError } = await supabase
        .from('staff_hours')
        .select('*')
        .eq('staff_id', member.id)
        .maybeSingle();
      if (hoursError && hoursError.code !== 'PGRST116') throw hoursError;

      setFormData({
        email: member.email,
        name: member.name,
        phone: member.phone || '',
        title: member.title || '',
        description: member.description || '',
        specialties: member.specialties || [],
        settings: member.settings || defaultFormData.settings,
        services:
          staffServices?.map((s) => ({
            id: s.service_id,
            price: s.price?.toString() ?? '',
            duration: s.duration?.toString() ?? '',
            is_active: s.is_active,
          })) || [],
        hours: staffHours
          ? {
              use_business_hours: false,
              regular_hours: staffHours.regular_hours,
              special_dates: staffHours.special_dates,
            }
          : defaultFormData.hours,
        password: '',
        profile_image: null,
      });
      setShowForm(true);
    } catch (err) {

      toast.error('שגיאה בטעינת נתוני איש הצוות');
    }
  };

  const handleSubmit = async () => {
    if (!businessId) return;

    const validationError = validateStaffForm(formData);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (formBusy) return; // הגנה מדאבל-קליק
    setFormBusy(true);

    try {
      if (editingStaff) {
        // --- עדכון קיים ---
        let profileImageUrl = editingStaff.profile_image_url;

        if (formData.profile_image) {
          const fileExt = formData.profile_image.name.split('.').pop();
          const filePath = `${businessId}/staff/${Math.random().toString(36).slice(2)}${Date.now()}.${fileExt}`;
          const { error: uploadErr } = await supabase.storage.from('profiles').upload(filePath, formData.profile_image);
          if (uploadErr) throw uploadErr;
          const { data } = supabase.storage.from('profiles').getPublicUrl(filePath);
          profileImageUrl = data.publicUrl;
        }

        const { error: updateErr } = await supabase
          .from('users')
          .update({
            name: formData.name,
            phone: formData.phone,
            title: formData.title,
            description: formData.description,
            specialties: formData.specialties,
            settings: formData.settings,
            profile_image_url: profileImageUrl,
          })
          .eq('id', editingStaff.id);
        if (updateErr) throw updateErr;

        // שירותים
        const { error: delServicesErr } = await supabase.from('staff_services').delete().eq('staff_id', editingStaff.id);
        if (delServicesErr) throw delServicesErr;

        for (const s of formData.services) {
          if (s.is_active) {
            const { error: insErr } = await supabase.from('staff_services').insert({
              staff_id: editingStaff.id,
              service_id: s.id,
              price: s.price ? parseFloat(s.price) : null,
              duration: s.duration || null,
              is_active: true,
            });
            if (insErr) throw insErr;
          }
        }

        // שעות
        if (!formData.hours.use_business_hours) {
          const { error: delHoursErr } = await supabase.from('staff_hours').delete().eq('staff_id', editingStaff.id);
          if (delHoursErr) throw delHoursErr;

          const { error: insHoursErr } = await supabase.from('staff_hours').insert({
            staff_id: editingStaff.id,
            regular_hours: (formData as any).hours?.regular_hours ?? null,
            special_dates: (formData as any).hours?.special_dates ?? [],
          });
          if (insHoursErr) throw insHoursErr;
        } else {
          // אם בחר "שעות עסק" — נמחק רשומת שעות מותאמות (אם קיימת)
          await supabase.from('staff_hours').delete().eq('staff_id', editingStaff.id);
        }

        toast.success('איש הצוות עודכן בהצלחה');
      } else {
        // --- יצירת איש צוות חדש ---
        // אין שום insert ידני ל-public.users בצד הלקוח!
        // אנו קוראים אך ורק ל-Edge Function שמייצר/מודד ב-Auth ואז upsert ל-users.
        let token: string | null = null;
        if (supabase.auth.getSession) {
          const { data } = await supabase.auth.getSession();
          token = data?.session?.access_token || null;
        }
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(
          'https://nkuqcyelxgyihrxyvitb.supabase.co/functions/v1/create-staff',
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              email: formData.email,
              password: formData.password, // אופציונלי; הפונקציה תייצר אם חסר
              name: formData.name,
              phone: formData.phone,
              business_id: businessId,
            }),
          }
        );

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result?.error || 'שגיאה ביצירת איש צוות');
        }

        const newStaffId: string = result?.user?.id;
        if (!newStaffId) {
          throw new Error('שגיאה: לא התקבל מזהה משתמש מהשרת');
        }

        // תמונה (עדכון שדה בלבד)
        if (formData.profile_image) {
          const fileExt = formData.profile_image.name.split('.').pop();
          const filePath = `${businessId}/staff/${Math.random().toString(36).slice(2)}${Date.now()}.${fileExt}`;
          const { error: uploadErr } = await supabase.storage.from('profiles').upload(filePath, formData.profile_image);
          if (uploadErr) throw uploadErr;
          const { data } = supabase.storage.from('profiles').getPublicUrl(filePath);
          const profileImageUrl = data.publicUrl;

          await supabase
            .from('users')
            .update({ profile_image_url: profileImageUrl })
            .eq('id', newStaffId);
        }

        // שירותים
        for (const s of formData.services) {
          if (s.is_active) {
            const { error: insErr } = await supabase.from('staff_services').insert({
              staff_id: newStaffId,
              service_id: s.id,
              price: s.price ? parseFloat(s.price) : null,
              duration: s.duration || null,
              is_active: true,
            });
            if (insErr) throw insErr;
          }
        }

        // שעות
        if (!formData.hours.use_business_hours) {
          const { error: hoursErr } = await supabase.from('staff_hours').insert({
            staff_id: newStaffId,
            regular_hours: (formData as any).hours?.regular_hours ?? null,
            special_dates: (formData as any).hours?.special_dates ?? [],
          });
          if (hoursErr) throw hoursErr;
        } else {
          // אם בחר "שעות עסק" – אין צורך ברשומה ייעודית
        }

        // עדכן completed ל-1
        await supabase
          .from('users')
          .update({ completed: 1 })
          .eq('id', newStaffId);

        toast.success('איש הצוות נוסף בהצלחה');
      }

      // ניקוי וטעינה מחדש (Realtime גם יעדכן, אבל נטען מיד)
      setShowForm(false);
      setEditingStaff(null);
      setFormData(defaultFormData);
      if (businessId) await loadData(businessId);
    } catch (e: any) {

      toast.error(e?.message || 'שגיאה בשמירת איש הצוות');
    } finally {
      setFormBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    // אל תמחק את המשתמש הנוכחי!
    if (id === user?.id) {
      toast.error('לא ניתן למחוק את עצמך מהמערכת');
      return;
    }
    const staffToDelete = staff.find((s) => s.id === id);
    if (staffToDelete?.role === 'admin') {
      toast.error('לא ניתן למחוק את המנהל הראשי');
      return;
    }
    if (!confirm('האם אתה בטוח שברצונך למחוק את איש הצוות?')) return;

    try {
      // עדכן את כל התורים של איש הצוות ל-null
      const { error: updateAppointmentsError } = await supabase
        .from('appointments')
        .update({ staff_id: null })
        .eq('staff_id', id);
      if (updateAppointmentsError) throw updateAppointmentsError;

      // מחק את איש הצוות
      const { error } = await supabase.from('users').delete().eq('id', id);
      if (error) throw error;
      toast.success('איש הצוות נמחק בהצלחה');

      // נטען מחדש, Realtime גם יעדכן
      if (businessId) await loadData(businessId);
    } catch (e) {

      toast.error('שגיאה במחיקת איש הצוות');
    }
  };

  const noValidSub = !planKey || !status;
  const usagePct = allowedStaff > 0 ? Math.min(100, Math.round((staffCount / allowedStaff) * 100)) : 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 -mx-4 px-4 shadow-sm">
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Link to="/settings" className="p-2 -mr-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all active:scale-95">
              <ArrowRight className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">צוות</h1>
              <p className="text-sm text-gray-500 mt-0.5">ניהול אנשי הצוות שלך</p>
            </div>
          </div>
          <button
            onClick={() => {
              if (!canAddStaff) {
                toast.error('הגעת למגבלת אנשי הצוות בתכנית שלך. לשדרוג, עבור/י לעמוד התמחור.');
                navigate('/plan-upgrade?reason=seats');
                return;
              }
              setEditingStaff(null);
              setFormData(defaultFormData);
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:scale-95 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canAddStaff || formBusy}
          >
            <Plus className="h-5 w-5" />
            <span className="text-sm font-semibold">הוסף איש צוות</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {(seatLoading || loadingStaff) && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">טוען נתוני צוות...</p>
            </div>
          </div>
        )}

        {seatError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700 text-sm text-center">{seatError}</p>
          </div>
        )}

        {!seatLoading && noValidSub && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-8 text-center mb-6 shadow-sm">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-amber-900 mb-2">אין תכנית פעילה</h3>
              <p className="text-sm text-amber-700 mb-6">יש לבחור תכנית כדי להוסיף אנשי צוות</p>
              <button
                className="px-6 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all shadow-md"
                onClick={() => navigate('/plan-upgrade')}
              >
                בחירת תכנית
              </button>
            </div>
          </div>
        )}

      <StaffFormModal
        isOpen={showForm}
        formData={formData}
        services={services}
        specialties={specialties}
        isEditing={!!editingStaff}
        onFormChange={setFormData}
        onSubmit={handleSubmit}
        onCancel={() => {
          setShowForm(false);
          setEditingStaff(null);
          setFormData(defaultFormData);
        }}
        existingImageUrl={editingStaff?.profile_image_url}
        busy={formBusy}
      />

        {staff.length === 0 && !loadingStaff && !seatLoading ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">אין אנשי צוות עדיין</h3>
            <p className="text-sm text-gray-500 mb-6">התחל בהוספת איש צוות ראשון</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {staff.map((member) => (
              <StaffCard
                key={member.id}
                member={member}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* כרטיס התכנית */}
        {!seatLoading && !noValidSub && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">התכנית שלך</h3>
                <p className="text-sm text-gray-500">ניהול מושבי צוות</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-4 py-1.5 rounded-full bg-blue-600 text-white font-bold text-sm">
                  {planKey === 'basic' && 'בסיסית'}
                  {planKey === 'medium' && 'בינונית'}
                  {planKey === 'vip' && 'VIP'}
                </span>
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full ${
                    status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : status === 'trialing'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {status === 'active' && 'פעילה'}
                  {status === 'trialing' && 'ניסיון'}
                  {status !== 'active' && status !== 'trialing' && status}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">מושבי צוות בשימוש</span>
                <span className="font-bold text-gray-900">
                  {allowedStaff > 0 ? `${staffCount} / ${allowedStaff}` : '0 / 0'}
                </span>
              </div>

              <div className="relative w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    usagePct >= 100 ? 'bg-red-500' : usagePct >= 80 ? 'bg-amber-500' : 'bg-blue-600'
                  }`}
                  style={{ width: `${Math.min(usagePct, 100)}%` }}
                />
              </div>

              <p className="text-xs text-gray-500">
                {planKey === 'basic' && 'מנהל בלבד - שדרג לתכנית גבוהה יותר להוספת צוות'}
                {planKey === 'medium' && 'עד 2 אנשי צוות'}
                {planKey === 'vip' && `עד ${allowedStaff} אנשי צוות`}
              </p>

              {!canAddStaff && allowedStaff > 0 && (
                <button
                  className="w-full mt-4 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold active:scale-[0.98] transition-all text-sm shadow-sm"
                  onClick={() => navigate('/plan-upgrade?reason=seats')}
                >
                  שדרג תכנית להוספת עוד צוות
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BusinessStaff;
